import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { useSearchParams } from 'react-router-dom';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { AuthenticateSelect } from '../../../../auth/select';
import { TextInput } from '../../../../form/textInput';
import { Select } from '../../../../form/select';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { ExpandedBlock } from '../../../expanded';
import { updateNodeValue } from '../../../../../services/method';
import { operationType, selectDataType } from '../../../../../services/types';
import { webURL } from '../../../../../services/API';
import { Main, Styled } from '../../tools/teams/style';

type Props = NodeProps<operationType> & { provider?: 'onedrive'; setChildMinimize?: any; setChildSettings?: any };

const eventOptions = ['created', 'updated', 'renamed', 'moved', 'deleted'];

function toSelectItems(items: any[] = [], activeId?: string) {
  return items.map((item, index) => ({
    id: item.id,
    refId: item.id,
    name: item.displayName || item.name || item.title || item.id || `Drive ${index + 1}`,
    isActive: activeId ? item.id === activeId : index === 0,
  }));
}

function getActiveId(items?: any[]) {
  return Array.isArray(items) ? (items.find(item => item.isActive)?.id || items[0]?.id || '') : '';
}

function getCollectionItems(response: any) {
  const data = response?.data?.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.value)) return data.value;
  if (Array.isArray(data)) return data;
  return [];
}

function normalizeFolderPath(path: string | undefined) {
  return String(path || '').trim();
}

function formatFolderPathLabel(path: string | undefined, fallbackName?: string) {
  const normalized = normalizeFolderPath(path);
  if (!normalized) return 'Drive root';
  if (fallbackName) return fallbackName;

  const rootMatch = normalized.match(/root:(.*)$/i);
  if (rootMatch?.[1]) return rootMatch[1].replace(/^\/+/, '') || 'Drive root';

  const segments = normalized.split('/').filter(Boolean);
  return segments[segments.length - 1] || normalized;
}

function buildFolderOptions(folders: any[], selectedId: string | undefined) {
  const currentId = String(selectedId || '').trim();
  const options: selectDataType[] = [
    { id: '', refId: 'drive-root', name: 'Entire drive (root)', isActive: !currentId },
    ...folders.map((folder: any) => ({
      id: folder.id,
      refId: folder.id,
      name: folder.name || folder.displayName || folder.id,
      isActive: currentId ? folder.id === currentId : false,
    })),
  ];

  if (currentId && !folders.some((folder: any) => folder.id === currentId)) {
    options.push({ id: currentId, refId: currentId, name: currentId, isActive: true });
  }

  return options;
}

function getMicrosoftActionError(response: any) {
  if (response?.data?.success !== false) return '';
  return response?.data?.errors?.[0]?.message || response?.data?.message || 'Microsoft returned an error.';
}

function getRequestError(error: any, fallback: string) {
  return error?.response?.data?.message
    || error?.response?.data?.errors?.[0]?.message
    || error?.response?.data?.error?.message
    || error?.message
    || fallback;
}

export function MicrosoftFileTriggerNode({ id, data, setChildMinimize, setChildSettings }: Props) {
  const { setNodes } = useReactFlow();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [expanded, setExpanded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [messageKind, setMessageKind] = React.useState<'info' | 'error'>('info');
  const [capabilities, setCapabilities] = React.useState<any>(null);
  const [folderOptions, setFolderOptions] = React.useState<any[]>([]);
  const [folderOptionsLoading, setFolderOptionsLoading] = React.useState(false);
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const folderLoadKeyRef = React.useRef('');

  const connectionId = data.value?.connectionId || data.value?.connection?.id || data.value?.connection?._id || '';
  const targetUser = data.value?.targetUser || '';
  const driveId = data.value?.driveId || '';
  const events = data.value?.events || eventOptions;
  const requiresTargetUser = capabilities?.appTargetUserMode === 'manual';

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setExpanded((value: boolean) => !value));
    if (setChildSettings) setChildSettings(() => setSettings(value => ({ ...value, isToggle: !value.isToggle })));
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value) {
      updateNodeValue(id, setNodes, {
        connection: null,
        connectionId: null,
        targetUser: '',
        driveId: '',
        rootItemId: '',
        rootPath: '',
        drives: [],
        events: eventOptions,
        status: 'disabled',
        memory: [],
      });
    }
  }, [data.value, id, setNodes]);

  React.useEffect(() => {
    if (!expanded) {
      setShowAdvanced(false);
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
    }
  }, [expanded]);

  React.useEffect(() => {
    if (!connectionId) {
      setCapabilities(null);
      return;
    }

    axios.get(`${webURL}/microsoft-files/connections/${connectionId}/capabilities`, {
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      params: { t: Date.now() },
    })
      .then(response => setCapabilities(response.data?.capabilities))
      .catch(() => setCapabilities(null));
  }, [connectionId]);

  const update = (values: any) => updateNodeValue(id, setNodes, values);

  const change = (event: any, key?: string) => {
    if (!key) return;
    const value = typeof event === 'string' ? event : event.target.value;

    if (key === 'targetUser' && value !== targetUser) {
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
      update({ targetUser: value, drives: [], driveId: '', rootItemId: '', rootPath: '' });
      return;
    }

    if (key === 'driveId' && value !== driveId) {
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
      update({ driveId: value, rootItemId: '', rootPath: '' });
      return;
    }

    update({ [key]: value });
  };

  const auth = (type: 'add' | 'remove', connection?: any) => {
    setFolderOptions([]);
    folderLoadKeyRef.current = '';

    if (type === 'add') {
      const nextConnectionId = connection?.id || connection?._id || null;
      update({
        connection,
        connectionId: nextConnectionId,
        ...(nextConnectionId === connectionId ? {} : { drives: [], driveId: '', rootItemId: '', rootPath: '' }),
      });
      return;
    }

    update({
      connection: null,
      connectionId: null,
      targetUser: '',
      drives: [],
      driveId: '',
      rootItemId: '',
      rootPath: '',
      status: 'disabled',
      fileSubscriptionId: '',
      fileExpirationDateTime: '',
      subscriptionId: '',
      expirationDateTime: '',
    });
  };

  const toggleEvent = (eventName: string) => {
    update({ events: events.includes(eventName) ? events.filter((value: string) => value !== eventName) : [...events, eventName] });
  };

  const handleSelectDrive = (selectId: any) => {
    const next = (data.value?.drives || []).map((item: any) => ({ ...item, isActive: item.id === selectId }));
    const selected = next.find((item: any) => item.isActive);
    setFolderOptions([]);
    folderLoadKeyRef.current = '';
    update({ drives: next, driveId: selected?.id || '', rootItemId: '', rootPath: '' });
  };

  const loadDrives = async () => {
    if (!connectionId || (requiresTargetUser && !targetUser)) return;
    setLoading(true);
    setMessage('');
    setMessageKind('info');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/onedrive`, {
        connectionId,
        targetUser,
        operation: 'list_drives',
      });
      const actionError = getMicrosoftActionError(response);
      if (actionError) throw new Error(actionError);
      const drives = toSelectItems(getCollectionItems(response), data.value?.driveId);
      const nextDriveId = getActiveId(drives);
      setFolderOptions([]);
      folderLoadKeyRef.current = '';
      update({ drives, driveId: nextDriveId, rootItemId: '', rootPath: '' });
      if (!drives.length) setMessage('No drives were returned for this connection.');
    } catch (error: any) {
      setMessageKind('error');
      setMessage(getRequestError(error, 'Unable to load drives'));
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async (nextDriveId?: string) => {
    const selectedDriveId = String(nextDriveId || driveId || '').trim();
    if (!connectionId || !selectedDriveId || (requiresTargetUser && !targetUser)) return;
    setFolderOptionsLoading(true);
    setMessage('');
    setMessageKind('info');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/actions/onedrive`, {
        connectionId,
        targetUser,
        operation: 'list_children',
        driveId: selectedDriveId,
        itemId: '',
      });
      const actionError = getMicrosoftActionError(response);
      if (actionError) throw new Error(actionError);
      const items = getCollectionItems(response);
      const folders = items.filter((item: any) => item?.itemType === 'folder' || item?.folder);
      setFolderOptions(folders);
      if (!folders.length) setMessage('No folders were found in the drive root.');
    } catch (error: any) {
      setFolderOptions([]);
      setMessageKind('error');
      setMessage(getRequestError(error, 'Unable to load folders'));
    } finally {
      setFolderOptionsLoading(false);
    }
  };

  React.useEffect(() => {
    if (!expanded || !connectionId || !driveId || (requiresTargetUser && !targetUser)) {
      if (!driveId) {
        setFolderOptions([]);
        folderLoadKeyRef.current = '';
      }
      return;
    }

    const nextKey = `${connectionId}:${targetUser}:${driveId}`;
    if (folderLoadKeyRef.current === nextKey) return;
    folderLoadKeyRef.current = nextKey;
    loadFolders(driveId);
  }, [connectionId, driveId, expanded, requiresTargetUser, targetUser]);

  const enable = async () => {
    if (!projectId || !connectionId || !driveId || (requiresTargetUser && !targetUser)) return;
    setLoading(true);
    setMessage('');
    setMessageKind('info');
    try {
      const response = await axios.put(`${webURL}/microsoft-files/triggers/${projectId}/${id}`, {
        connectionId,
        targetUser,
        driveId: data.value?.driveId,
        rootItemId: data.value?.rootItemId,
        rootPath: data.value?.rootPath,
        events,
      });
      const trigger = response.data?.trigger;
      const nextSubscriptionId = trigger?.fileSubscriptionId || trigger?.subscriptionId || '';
      const nextExpiration = trigger?.fileExpirationDateTime || trigger?.expirationDateTime || '';
      update({
        status: trigger?.status || 'active',
        fileSubscriptionId: nextSubscriptionId,
        fileExpirationDateTime: nextExpiration,
        subscriptionId: nextSubscriptionId,
        expirationDateTime: nextExpiration,
      });
      setMessage('OneDrive trigger enabled. New file changes will start workflow runs.');
    } catch (error: any) {
      setMessageKind('error');
      setMessage(getRequestError(error, 'Unable to enable the OneDrive trigger'));
      update({ status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    if (!projectId) return;
    setLoading(true);
    setMessage('');
    setMessageKind('info');
    try {
      await axios.delete(`${webURL}/microsoft-files/triggers/${projectId}/${id}`);
      update({
        status: 'disabled',
        fileSubscriptionId: '',
        fileExpirationDateTime: '',
        subscriptionId: '',
        expirationDateTime: '',
      });
      setMessage('Trigger disabled.');
    } catch (error: any) {
      setMessageKind('error');
      setMessage(getRequestError(error, 'Unable to disable the OneDrive trigger'));
    } finally {
      setLoading(false);
    }
  };

  const checkNow = async () => {
    if (!projectId) return;
    setLoading(true);
    setMessage('');
    setMessageKind('info');
    try {
      const response = await axios.post(`${webURL}/microsoft-files/triggers/${projectId}/${id}/check`);
      setMessage(`Check completed: ${response.data?.result?.events || 0} file event(s).`);
    } catch (error: any) {
      setMessageKind('error');
      setMessage(getRequestError(error, 'Unable to check OneDrive for changes'));
    } finally {
      setLoading(false);
    }
  };

  const activeSubscriptionId = data.value?.fileSubscriptionId || data.value?.subscriptionId || '';
  const activeExpiration = data.value?.fileExpirationDateTime || data.value?.expirationDateTime || '';
  const isConnected = Boolean(activeSubscriptionId && data.value?.status === 'active');
  const isError = data.value?.status === 'error';
  const warning = !connectionId || !driveId || (requiresTargetUser && !targetUser) || isError;
  const enableDisabled = loading || !connectionId || !driveId || (requiresTargetUser && !targetUser) || !events.length;
  const disableDisabled = loading || (!activeSubscriptionId && data.value?.status !== 'active');
  const driveOptions = data.value?.drives || [];
  const normalizedRootPath = normalizeFolderPath(data.value?.rootPath);
  const selectedDrive = driveOptions.find((item: any) => item.isActive || item.id === driveId);
  const selectedFolder = folderOptions.find((item: any) => item.id === data.value?.rootItemId);
  const locationName = data.value?.rootItemId
    ? selectedFolder?.name || formatFolderPathLabel(normalizedRootPath) || 'Selected folder'
    : 'Entire drive';
  const rawStatus = String(data.value?.status || 'disabled');
  const statusText = isConnected ? 'Connected' : isError ? 'Error' : rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1);

  const sectionStyle: React.CSSProperties = {
    border: '1px solid rgb(220, 220, 220)',
    borderRadius: 12,
    padding: '12px 14px',
    background: '#fff',
    marginBottom: 12,
  };
  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '0 0 10px',
    fontSize: 14,
    color: '#111827',
  };
  const stepStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    background: '#e8f1fb',
    color: '#0364b8',
    fontSize: 12,
    fontWeight: 700,
  };
  const eventGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(105px, 1fr))',
    gap: 8,
  };
  const eventChip = (active: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    borderRadius: 10,
    border: active ? '1px solid #0364b8' : '1px solid rgb(215, 215, 215)',
    background: active ? 'rgba(3, 100, 184, 0.08)' : '#fff',
    cursor: 'pointer',
    fontSize: 14,
    color: '#374151',
  });
  const locationPreviewStyle: React.CSSProperties = {
    marginTop: 10,
    padding: '10px 12px',
    borderRadius: 10,
    border: '1px solid #d7e7f6',
    background: '#f7fbff',
  };
  const statusCardStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    padding: '11px 12px',
    marginBottom: 12,
    borderRadius: 10,
    border: `1px solid ${isError ? '#fecaca' : isConnected ? '#bbf7d0' : '#e5e7eb'}`,
    background: isError ? '#fef2f2' : isConnected ? '#f0fdf4' : '#f9fafb',
  };

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {warning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : isConnected ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {expanded ? (
          <ExpandedBlock data={{ id, ...data }} handleClose={() => setExpanded(false)} isWarning={warning} style={{ minWidth: '420px', maxWidth: '500px' }}>
            <Styled style={{ maxWidth: '500px' }}>
              <div style={sectionStyle}>
                <h5 style={sectionTitleStyle}><span style={stepStyle}>1</span> Connect Microsoft</h5>
                <AuthenticateSelect type="microsoft" defaultId={connectionId} defaultConnection={data.value?.connection} handleAuthenticate={auth} background={data.background} />

                {requiresTargetUser ? (
                  <>
                    <TextInput
                      keyRef="targetUser"
                      value={targetUser}
                      title="User"
                      placeholder="user@company.com"
                      handleChange={change}
                    />
                    <small style={{ display: 'block', color: '#6b7280' }}>Enter the user whose OneDrive should be monitored.</small>
                  </>
                ) : null}
              </div>

              <div style={sectionStyle}>
                <h5 style={sectionTitleStyle}><span style={stepStyle}>2</span> Choose a location</h5>

                <div className="action" style={{ marginBottom: 10 }}>
                  <button className="btn1 withBorder" onClick={loadDrives} disabled={!connectionId || loading || (requiresTargetUser && !targetUser)}>
                    {loading ? 'Loading...' : driveOptions.length ? 'Refresh drives' : 'Load drives'}
                  </button>
                  <button className="btn1 withBorder" onClick={() => loadFolders()} disabled={!connectionId || loading || folderOptionsLoading || !driveId || (requiresTargetUser && !targetUser)}>
                    {folderOptionsLoading ? 'Loading...' : folderOptions.length ? 'Refresh folders' : 'Load folders'}
                  </button>
                </div>

                <Select title="Drive" data={driveOptions} handleSelectItem={handleSelectDrive} isSmall emptyMsg={loading ? 'Loading drives...' : 'Load drives to continue'} />
                <Select
                  title="Folder"
                  data={buildFolderOptions(folderOptions, data.value?.rootItemId)}
                  handleSelectItem={(selectId) => {
                    const nextFolder = folderOptions.find((folder: any) => folder.id === selectId);
                    update({
                      rootItemId: selectId || '',
                      rootPath: selectId ? normalizeFolderPath(nextFolder?.path) : '',
                    });
                  }}
                  isSmall
                  emptyMsg={folderOptionsLoading ? 'Loading folders...' : driveId ? 'Entire drive (root)' : 'Choose a drive first'}
                />

                {driveId ? (
                  <div style={locationPreviewStyle}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#0364b8', marginBottom: 4 }}>Monitoring</div>
                    <div style={{ fontSize: 14, color: '#111827', fontWeight: 600 }}>
                      {selectedDrive?.name || 'OneDrive'} <span style={{ color: '#9ca3af', fontWeight: 400 }}>›</span> {locationName}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 12, color: '#6b7280' }}>
                      {normalizedRootPath || 'All folders and files in this drive'}
                    </div>
                  </div>
                ) : null}

                <button
                  type="button"
                  className="btn1"
                  onClick={() => setShowAdvanced(value => !value)}
                  style={{ marginTop: 10, paddingLeft: 0, color: '#4b5563', fontSize: 13 }}
                >
                  {showAdvanced ? 'Hide advanced settings' : 'Advanced settings'}
                </button>

                {showAdvanced ? (
                  <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                    <TextInput keyRef="driveId" value={driveId} title="Drive ID" placeholder="drive id" handleChange={change} isNormal />
                    <TextInput
                      title="Folder ID"
                      value={data.value?.rootItemId || ''}
                      placeholder="Leave blank for the drive root"
                      handleChange={(event: any) => update({ rootItemId: event.target.value })}
                      isNormal
                    />
                    <TextInput
                      title="Folder path"
                      value={data.value?.rootPath || ''}
                      placeholder="/drives/.../root:/Folder"
                      handleChange={(event: any) => update({ rootPath: event.target.value })}
                      isNormal
                    />
                  </div>
                ) : null}
              </div>

              <div style={sectionStyle}>
                <h5 style={sectionTitleStyle}><span style={stepStyle}>3</span> Select file events</h5>
                <div style={eventGridStyle}>
                  {eventOptions.map(eventName => (
                    <label key={eventName} style={eventChip(events.includes(eventName))}>
                      <input type="checkbox" checked={events.includes(eventName)} onChange={() => toggleEvent(eventName)} />
                      <span>{eventName.charAt(0).toUpperCase() + eventName.slice(1)}</span>
                    </label>
                  ))}
                </div>
                <small style={{ display: 'block', marginTop: 10, color: '#6b7280' }}>
                  Folder-only changes are ignored. Each changed file starts one workflow run.
                </small>
              </div>

              <div style={statusCardStyle}>
                <div>
                  <div className={`status ${isConnected ? 'connected' : isError ? 'error' : ''}`} style={{ fontSize: 14, fontWeight: 600 }}>
                    {statusText}
                  </div>
                  <div style={{ marginTop: 3, fontSize: 12, color: '#6b7280' }}>
                    {isConnected
                      ? `Listening for OneDrive changes${activeExpiration ? ` · renews ${new Date(activeExpiration).toLocaleDateString()}` : ''}`
                      : isError
                        ? 'The trigger could not be enabled. See the message below.'
                        : 'Enable when the connection and location are ready.'}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: isConnected ? '#15803d' : isError ? '#b91c1c' : '#6b7280', fontWeight: 600 }}>
                  {isConnected ? 'Live' : isError ? 'Needs attention' : 'Not active'}
                </span>
              </div>

              {message ? (
                <div
                  className="message"
                  role={messageKind === 'error' ? 'alert' : 'status'}
                  style={{
                    marginBottom: 12,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: `1px solid ${messageKind === 'error' ? '#fecaca' : '#dbeafe'}`,
                    background: messageKind === 'error' ? '#fef2f2' : '#eff6ff',
                    color: messageKind === 'error' ? '#991b1b' : '#1e40af',
                    fontSize: 13,
                    lineHeight: 1.45,
                  }}
                >
                  {message}
                </div>
              ) : null}

              <div className="action">
                <button type="button" className="btn1 withBorder" disabled={enableDisabled} onClick={enable}>
                  {loading ? 'Please wait...' : isConnected ? 'Update trigger' : 'Enable trigger'}
                </button>
                <button type="button" className="btn1 withBorder" disabled={loading || !isConnected} onClick={checkNow}>Check now</button>
                {isConnected || data.value?.status === 'active' ? (
                  <button type="button" className="btn1 withBorder danger" disabled={disableDisabled} onClick={disable}>Disable</button>
                ) : null}
              </div>
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
